import { useDispatch, useSelector } from 'react-redux'
import styled from 'astroturf/react'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
const colorTextDestructive = 'var(--colorTextDestructive, var(--coreColorRed700))'
const colorTextSecondary = 'var(--colorTextSecondary, var(--coreColorNeutral600))'
import { OnetrustSettingsButton } from '@calendly/onetrust/redux'
import { usePageTitle } from '@calendly/hooks'
import { useFlag } from '@calendly/feature-check-react'
import {
  SpotPickerWrapper as Wrapper,
  SpotPickerTitle as Title,
  SpotPickerRow as Row,
  DiagnosticsContainer as Container,
  Diagnostics
} from '@calendly/booking-kit'
import { TitleText } from '@calendly/ui/components/typography'
import { Button } from '@calendly/ui/components/button'

import { useI18n } from 'locales'
import Footer from 'components/select_date_time/footer'
import {
  isPollSelector
} from 'selectors/event_type'
import { currentInviteeSelector } from 'selectors/current_invitee'
import { loadingProgressSelector } from 'selectors'
import { isDiagnosticsAvailableSelector, isDiagnosticsEnabledSelector } from 'selectors/diagnostics'
import { daySelector } from 'selectors/calendar'
import { currentUserLocaleSelector } from 'selectors/user'
import { isEmbeddedSelector } from 'selectors/embed'
import { setDiagnosticsEnabled } from 'actions/diagnostics'
import { sessionSelector } from 'root-api'
import { useDownloadBookingTracing } from 'hooks/useDownloadBookingTracing'

import ProgressBar from './progress_bar'
import Dates from './dates'
import Times from './times'
import _PaddingWrapper from "spotpicker-PaddingWrapper.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/spotpicker.js?styleId=PaddingWrapper";
import _CookieSettingsButton from "spotpicker-CookieSettingsButton.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/spotpicker.js?styleId=CookieSettingsButton";
import _DiagnosticsRow from "spotpicker-DiagnosticsRow.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/spotpicker.js?styleId=DiagnosticsRow";
import _DiagnosticsContent from "spotpicker-DiagnosticsContent.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/spotpicker.js?styleId=DiagnosticsContent";
import _DownloadButtonContainer from "spotpicker-DownloadButtonContainer.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/spotpicker.js?styleId=DownloadButtonContainer";
import _TracingErrorMessage from "spotpicker-TracingErrorMessage.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/spotpicker.js?styleId=TracingErrorMessage";
import _StyledInline from "spotpicker-StyledInline.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/spotpicker.js?styleId=StyledInline";
import _LinksContainer from "spotpicker-LinksContainer.module.css!=!astroturf/inline-loader?style=1!/app/frontend/components/select_date_time/spotpicker/spotpicker.js?styleId=LinksContainer";


const PaddingWrapper = /*#__PURE__*/styled('div', null, {
  displayName: "PaddingWrapper",
  styles: _PaddingWrapper,
  attrs: props => ({
    ...props,
    isPhone: useDeviceType('isPhone')
  })
})

const CookieSettingsButton = /*#__PURE__*/styled(OnetrustSettingsButton, null, {
  displayName: "CookieSettingsButton",
  styles: _CookieSettingsButton,
  attrs: props => ({
    ...props,
    isPhone: useDeviceType('isPhone')
  })
})

const DiagnosticsRow = /*#__PURE__*/styled('div', null, {
  displayName: "DiagnosticsRow",
  styles: _DiagnosticsRow,
  attrs: props => ({
    ...props,
    isPhone: useDeviceType('isPhone')
  })
})

const DiagnosticsContent = /*#__PURE__*/styled('div', null, {
  displayName: "DiagnosticsContent",
  styles: _DiagnosticsContent
})

const DownloadButtonContainer = /*#__PURE__*/styled('div', null, {
  displayName: "DownloadButtonContainer",
  styles: _DownloadButtonContainer,
  attrs: props => ({
    ...props,
    isPhone: useDeviceType('isPhone')
  })
})

const TracingErrorMessage = /*#__PURE__*/styled('div', null, {
  displayName: "TracingErrorMessage",
  styles: _TracingErrorMessage
})

const StyledInline = /*#__PURE__*/styled('span', null, {
  displayName: "StyledInline",
  styles: _StyledInline,
  attrs: props => ({
    ...props,
    isPhone: useDeviceType('isPhone')
  })
})

const LinksContainer = /*#__PURE__*/styled('div', null, {
  displayName: "LinksContainer",
  styles: _LinksContainer
})

const checkIfIframe = () => window.location !== window.parent.location

const FooterLinks = () => {
  const embedSelector = useSelector(isEmbeddedSelector)
  const isEmbedded = checkIfIframe() || embedSelector

  if (isEmbedded) return <CookieSettingsButton />

  return (
    <StyledInline>
      <CookieSettingsButton />
    </StyledInline>
  )
}

const Spotpicker = () => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const day = useSelector(daySelector)
  const invitee = useSelector(currentInviteeSelector)
  const loadingProgress = useSelector(loadingProgressSelector)
  const diagnosticsEnabled = useSelector(isDiagnosticsEnabledSelector)
  const available = useSelector(isDiagnosticsAvailableSelector)
  const locale = useSelector(currentUserLocaleSelector)
  const isPoll = useSelector(isPollSelector)
  const isTabletUp = useDeviceType('isTabletUp')
  const isTabletDown = useDeviceType('isTabletDown')
  const bookingAppRedesign = useFlag('booking_app_redesign', false)
  const hasSelectedDay = !!day
  const displayDates = isTabletUp || !hasSelectedDay

  const session = useSelector(sessionSelector)
  const isPretending = session?.current?.is_pretending || false

  // Custom hook for downloading booking tracing data
  const { downloadTracing, isDownloading, error: downloadError } = useDownloadBookingTracing()

  const titleText = isPoll
    ? i18n.t('booking.templates.poll.select_date_and_time')
    : i18n.t('booking.templates.select_date_and_time')
  usePageTitle(`${titleText} - Calendly`)

  const onEnableDiagnostics = () => dispatch(setDiagnosticsEnabled(true))
  const onDisableDiagnostics = () => dispatch(setDiagnosticsEnabled(false))
  const onClickHintDone = () => dispatch(setDiagnosticsEnabled(false))

  return (
    <Wrapper hasSelectedDay={hasSelectedDay} data-component="spotpicker">
      <ProgressBar loadingProgress={loadingProgress} calendarActive />
      {invitee && (
        <PaddingWrapper>
          <TitleText as="h1" size="small" color={colorTextSecondary}>{i18n.t('booking.templates.reschedule_event')}</TitleText>
        </PaddingWrapper>
      )}
      {!bookingAppRedesign && isTabletUp && <Title>{titleText}</Title>}
      <Row wide={isTabletDown && hasSelectedDay}>
        {displayDates && <Dates />}
        <Times />
      </Row>
      <Container narrow={!hasSelectedDay} diagnosticsEnabled={diagnosticsEnabled}>
        <div data-component="diagnostics">
          {!bookingAppRedesign && (
            <DiagnosticsRow>
              <DiagnosticsContent>
                <Diagnostics
                  available={available}
                  locale={locale}
                  enabled={diagnosticsEnabled}
                  onClickHintDone={onClickHintDone}
                  onEnableDiagnostics={onEnableDiagnostics}
                  onDisableDiagnostics={onDisableDiagnostics}
                />
              </DiagnosticsContent>
              {available && isPretending && (
                <DownloadButtonContainer>
                  <Button
                    onClick={downloadTracing}
                    disabled={isDownloading}
                    decoration="secondary-outline"
                  >
                    {isDownloading ? 'Downloading...' : 'Download Tracing Data'}
                  </Button>
                  {downloadError && (
                    <TracingErrorMessage role="alert">{downloadError}</TracingErrorMessage>
                  )}
                </DownloadButtonContainer>
              )}
            </DiagnosticsRow>
          )}
          {!diagnosticsEnabled && isTabletDown && (
            <LinksContainer>
              <FooterLinks />
            </LinksContainer>
          )}
        </div>
      </Container>
      <Footer />
    </Wrapper>
  )
}

export default Spotpicker
